import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import Tooltip from '@mui/material/Tooltip';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { STACK_TOOLTIPS } from './stackTooltips';
import './Mission.css';

const Mission: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const isMobile = useMediaQuery('(hover: none)');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTech, setActiveTech] = useState<string | null>(null);

  const handleTagClick = (tech: string) => {
    if (isMobile && STACK_TOOLTIPS[tech]) {
      setActiveTech(tech);
      setDrawerOpen(true);
    }
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setActiveTech(null);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section id="mission" className="mission section-padding">
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.div className="section-header" variants={itemVariants}>
            <h2 className="section-title">Mission actuelle</h2>
            <p className="section-subtitle">Développeur front-end en mission freelance depuis juin 2025</p>
          </motion.div>

          <div className="mission-content">
            {/* Intro */}
            <motion.div className="mission-intro" variants={itemVariants}>
              <p>
                Je développe le front d'une <strong>plateforme SaaS métier</strong> complexe,
                couvrant la gestion de projets, la facturation et l'administration.
              </p>
              <p>
                Basée sur <strong>React 19 / TypeScript</strong> strict / <strong>MUI 7</strong>,
                l'application s'appuie sur un design system interne et des composants réutilisables
                pour garantir cohérence et scalabilité.
              </p>
              <p>
                Je travaille en interaction avec une <strong>API REST Java / Spring Boot</strong>,
                avec un focus sur la maintenabilité du code et la qualité de l'expérience utilisateur.
              </p>
            </motion.div>

            {/* Architecture & state */}
            <motion.div className="mission-block" variants={itemVariants}>
              <h3>Architecture &amp; State</h3>
              <ul>
                <li>
                  State serveur avec <strong>TanStack Query</strong> : query key factory typée,
                  invalidations par prédicat, mises à jour optimistes.
                </li>
                <li>
                  State UI avec <strong>Zustand</strong> (~37 stores) : séparation des préoccupations (sélection, édition, clipboard, locks, historique, formatage).
                </li>
                <li>
                  Routing <strong>React Router 7</strong> avec layout routes, route guards de permissions et bloqueur de navigation pour formulaires non sauvegardés.
                </li>
                <li>
                  Intégration <strong>API REST Java (Spring Boot)</strong> avec couche HTTP Axios,
                  gestion centralisée des erreurs (offline detection, timeouts, OperationErrorDialog),
                  authentification <strong>Firebase</strong> (email + Google OAuth + 2FA TOTP).
                </li>
              </ul>
            </motion.div>

            {/* Réalisations techniques majeures */}
            <motion.div className="mission-block" variants={itemVariants}>
              <h3>Réalisations techniques majeures</h3>

              <div className="mission-cards">
                <div className="mission-card">
                  <div className="mission-card-icon">
                    <i className="fas fa-table"></i>
                  </div>
                  <h4>Tableur virtualisé</h4>
                  <p>
                    Type Excel, sous-système majeur de l'application, TanStack Virtual avec 3 rails
                    de colonnes (gauche pinnable / centre / droite pinnable), édition Lexical
                    (rich-text) dans les cellules, copier/coller Excel-like, filtres
                    multi-colonnes, tri, locks d'édition cellule par cellule en temps réel, undo/redo cross-tab.
                  </p>
                </div>

                <div className="mission-card">
                  <div className="mission-card-icon">
                    <i className="fas fa-edit"></i>
                  </div>
                  <h4>Éditeur Lexical personnalisé</h4>
                  <p>
                    Synchronisation de 3 éditeurs (cellule, toolbar, panneau détail) sur un draft
                    partagé. DecoratorNodes custom pour variables dynamiques dans des templates (email).
                    Modes plain-text/rich-text, sérialisation JSON bidirectionnelle.
                  </p>
                </div>

                <div className="mission-card">
                  <div className="mission-card-icon">
                    <i className="fas fa-undo-alt"></i>
                  </div>
                  <h4>Undo/redo</h4>
                  <p>
                    Système propre à chaque projet, couvrant les quatre types d'actions
                    (édition, collage, coupe, réorganisation de lignes). L'historique conserve
                    l'état complet avant/après et reste cohérent même sur plusieurs onglets
                    ou après ajout/suppression de lignes.
                  </p>
                </div>

                <div className="mission-card">
                  <div className="mission-card-icon">
                    <i className="fas fa-sync-alt"></i>
                  </div>
                  <h4>Temps réel STOMP/WebSocket</h4>
                  <p>
                    Locks d'édition par cellule, reconnexion exponentielle avec heartbeat,
                    déduplication des événements émis localement.
                  </p>
                </div>

                <div className="mission-card">
                  <div className="mission-card-icon">
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <h4>Permissions à 3 dimensions</h4>
                  <p>
                    Rôle global / override projet / liaison entité avec hooks <code>useCan</code> / <code>useCanOnProject</code> et <code>PermissionGuard</code> (masquage, jamais désactivation).
                  </p>
                </div>

                <div className="mission-card">
                  <div className="mission-card-icon">
                    <i className="fas fa-tachometer-alt"></i>
                  </div>
                  <h4>Optimisation mémoire</h4>
                  <p>
                    Élimination systématique des fuites mémoire (cleanup refs, listeners nommés,
                    auto-stop d'intervalles, defer du measureElement de TanStack Virtual).
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Stack technique */}
            <motion.div className="mission-stack" variants={itemVariants}>
              <h3>Stack technique</h3>
              <p className="mission-tags-hint">
                {isMobile
                  ? 'Appuyez sur une technologie pour plus de détails'
                  : 'Survolez les technologies pour plus de détails'}
              </p>
              <div className="mission-tags">
                {[
                  'React 19', 'TypeScript', 'MUI 7', 'TanStack Query', 'TanStack Virtual',
                  'Zustand', 'React Router 7', 'Lexical', 'Axios', 'Firebase Auth',
                  'STOMP/WebSocket', 'ExcelJS', 'Spring Boot (API)'
                ].map((tech) => {
                  const tooltip = STACK_TOOLTIPS[tech];
                  const tag = (
                    <span
                      key={tech}
                      className="mission-tag"
                      tabIndex={tooltip ? 0 : undefined}
                      onClick={() => handleTagClick(tech)}
                    >
                      {tech}
                    </span>
                  );

                  if (!tooltip || isMobile) return tag;

                  return (
                    <Tooltip
                      key={tech}
                      title={tooltip}
                      arrow
                      placement="top"
                      enterDelay={200}
                      enterNextDelay={100}
                      slotProps={{
                        tooltip: {
                          sx: {
                            maxWidth: 320,
                            fontSize: '0.85rem',
                            lineHeight: 1.5,
                            padding: '0.75rem 1rem',
                            borderRadius: '8px',
                          },
                        },
                      }}
                    >
                      {tag}
                    </Tooltip>
                  );
                })}
              </div>

              <Drawer
                anchor="bottom"
                open={drawerOpen}
                onClose={handleDrawerClose}
                PaperProps={{
                  sx: {
                    borderRadius: '16px 16px 0 0',
                    maxHeight: '50vh',
                  },
                }}
              >
                {activeTech && (
                  <div className="stack-drawer">
                    <div className="stack-drawer-handle" />
                    <h4 className="stack-drawer-title">{activeTech}</h4>
                    <p className="stack-drawer-desc">{STACK_TOOLTIPS[activeTech]}</p>
                    <button
                      className="stack-drawer-close"
                      onClick={handleDrawerClose}
                    >
                      Fermer
                    </button>
                  </div>
                )}
              </Drawer>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Mission;
